import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  NgZone,
  type AfterViewInit,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import {
  catchError,
  concat,
  filter,
  first,
  fromEvent,
  interval,
  map,
  merge,
  of,
  skip,
  switchMap,
  tap,
  timeout,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { IS_FEATURE_FLAG_MANAGER_ENABLED } from '../../../shared/components/feature-flag-manager/feature-flag-definition.consts';
import { FeatureFlagManagerComponent } from '../../../shared/components/feature-flag-manager/feature-flag-manager.component';
import { ReloadThisPageModalComponent } from '../../../shared/components/modals/reload-this-page-modal/reload-this-page-modal.component';
import { Roles } from '../../../shared/consts/roles.enum';
import { SharedFacade } from '../../../shared/store/shared.facade';
import { ERROR_DETAILS_KEY } from '../../consts/error.consts';
import { getErrorDetails } from '../../helpers/get-error-details.helper';
import { DialogService } from '../../services/dialog.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { LoggerService, MessageLevel } from '../../services/logger.service';

// TODO: refactor

const SERVICE_WORKER_TAG = '#SERVICE_WORKER';

const DETAILS_KEY = 'Details';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FeatureFlagManagerComponent, RouterOutlet],
  selector: 'app-root-container',
  templateUrl: './app.container.html',
})
export class AppContainerComponent implements OnInit, AfterViewInit {
  readonly #applicationReference = inject(ApplicationRef);
  readonly #authService = inject(AuthService);
  readonly #destroyReference = inject(DestroyRef);
  readonly #dialog = inject(DialogService);
  readonly #logger = inject(LoggerService);
  readonly #ngZone = inject(NgZone);
  readonly #router = inject(Router);
  readonly #sharedFacade = inject(SharedFacade);
  readonly #swUpdate = inject(SwUpdate);
  readonly #title = inject(Title);
  readonly #translocoService = inject(TranslocoService);
  readonly #localStorage = inject(LocalStorageService);
  readonly #document = inject(DOCUMENT);

  readonly #languageChanges = toSignal(this.#translocoService.langChanges$);

  readonly #ROUTES_THAT_SHOULD_NOT_DISPLAY_UPDATE_MESSAGE = [
    'register-supplier',
  ];

  constructor() {
    effect(() => {
      this.#setDocumentLanguage(this.#languageChanges());
      this.#setPageTitle();
    });
  }

  readonly isFeatureFlagManagerEnabled =
    this.#localStorage.getItem('featureFlagManagerEnabled') === true &&
    IS_FEATURE_FLAG_MANAGER_ENABLED;

  ngOnInit(): void {
    this.#checkIfApplicationBecomesStableInReasonableTime();
    this.#initializeNetworkAccessTracking();
    this.#monitorServiceWorkerUpdates();
  }

  ngAfterViewInit(): void {
    this.#initializePendoIfEnabled();
  }

  #setDocumentLanguage(language: string | undefined): void {
    if (language !== undefined) {
      this.#document.documentElement.lang = language;
    }
  }

  #setPageTitle(): void {
    const translatedTitle = this.#translocoService.translate('core.title');
    this.#title.setTitle(translatedTitle);
  }

  #checkIfApplicationBecomesStableInReasonableTime(): void {
    this.#ngZone.runOutsideAngular(() => {
      this.#applicationReference.isStable
        .pipe(
          first((isStable) => isStable),
          timeout({
            // NOTE: 10 seconds
            first: 10_000,
            with: () => of(false),
          }),
          filter((isStable) => !isStable),
          tap(() => {
            if (!environment.suppressApplicationLifecycleLogging) {
              // TODO: make sure this does not happen very often
              this.#logger.log({
                level: MessageLevel.Error,
                message: 'Application is not stable after 10 seconds',
                data: {},
              });
            }
          }),
        )
        .subscribe();
    });
  }

  #initializeNetworkAccessTracking(): void {
    this.#ngZone.runOutsideAngular(() => {
      merge(
        of(navigator.onLine),
        fromEvent(globalThis, 'online').pipe(map(() => true)),
      )
        .pipe(
          filter((hasNetworkAccess) => hasNetworkAccess),
          skip(1),
          tap(() => {
            this.#ngZone.run(() => {
              this.#sharedFacade.enqueueSuccessSnackbar({
                translationKey: marker('shared.onlineMessage'),
              });
            });
          }),
          takeUntilDestroyed(this.#destroyReference),
        )
        .subscribe();
    });

    this.#ngZone.runOutsideAngular(() => {
      merge(
        of(navigator.onLine),
        fromEvent(globalThis, 'offline').pipe(map(() => false)),
      )
        .pipe(
          filter((hasNetworkAccess) => !hasNetworkAccess),
          tap(() => {
            this.#ngZone.run(() => {
              this.#sharedFacade.enqueueErrorSnackbar({
                translationKey: marker('shared.offlineMessage'),
              });
            });
          }),
          takeUntilDestroyed(this.#destroyReference),
        )
        .subscribe();
    });
  }

  // TODO: extract to a service
  #monitorServiceWorkerUpdates(): void {
    if (!this.#swUpdate.isEnabled) {
      return;
    }

    this.#checkForServiceWorkerUpdatesPeriodically();
    this.#listenForServiceWorkerVersionUpdatesEvents();
    this.#listenForServiceWorkerUnrecoverableStateEvents();
  }

  #checkForServiceWorkerUpdatesPeriodically(): void {
    this.#ngZone.runOutsideAngular(() => {
      concat(
        this.#applicationReference.isStable.pipe(
          first((isStable) => isStable),
          timeout({
            // NOTE: 1 minute
            first: 60_000,
            with: () => of(false),
          }),
        ),
        // NOTE: 1 hour
        interval(3_600_000),
      )
        .pipe(
          switchMap(async () => this.#swUpdate.checkForUpdate()),
          catchError((error: unknown) => {
            this.#logger.log({
              level: MessageLevel.Error,
              message: `Error while checking for application update ${SERVICE_WORKER_TAG}`,
              data: {
                [ERROR_DETAILS_KEY]: getErrorDetails(error),
              },
            });

            return of(false);
          }),
          takeUntilDestroyed(this.#destroyReference),
        )
        .subscribe();
    });
  }

  #listenForServiceWorkerVersionUpdatesEvents(): void {
    this.#ngZone.runOutsideAngular(() => {
      this.#swUpdate.versionUpdates
        .pipe(
          tap((versionEvent) => {
            if (
              this.#ROUTES_THAT_SHOULD_NOT_DISPLAY_UPDATE_MESSAGE.some(
                (route) => this.#router.url.includes(route),
              )
            ) {
              return;
            }

            switch (versionEvent.type) {
              case 'NO_NEW_VERSION_DETECTED': {
                // NOTE: do nothing

                break;
              }
              case 'VERSION_DETECTED': {
                if (!environment.suppressApplicationLifecycleLogging) {
                  this.#logger.log({
                    level: MessageLevel.Info,
                    message: `New application version detected ${SERVICE_WORKER_TAG}`,
                    data: {
                      [DETAILS_KEY]: {
                        versionEvent,
                      },
                    },
                  });
                }

                break;
              }
              case 'VERSION_READY': {
                if (!environment.suppressApplicationLifecycleLogging) {
                  this.#logger.log({
                    level: MessageLevel.Info,
                    message: `New application version is ready ${SERVICE_WORKER_TAG}`,
                    data: {
                      [DETAILS_KEY]: {
                        versionEvent,
                      },
                    },
                  });
                }

                this.#ngZone.run(() => {
                  if (!this.#authService.checkIfLocalBrowserDataIsValid()) {
                    // QUESTION: should we show a different message here?
                    this.#authService.removeInformationFromStorage();

                    void this.#router.navigate(['/login']);
                  }

                  const loggedInUserRole =
                    this.#authService.getLoggedInUserRole();

                  if (
                    loggedInUserRole === Roles.AUDITOR ||
                    loggedInUserRole === Roles.SUPPLIER
                  ) {
                    this.#dialog.open(ReloadThisPageModalComponent, {
                      width: '400px',
                    });
                  } else {
                    this.#sharedFacade.enqueueInfoSnackbar({
                      translationKey: marker('shared.versionReadyMessage'),
                      // TODO: move to a constant
                      priority: 1_000_000,
                      canBeDismissed: false,
                    });
                  }
                });

                break;
              }
              case 'VERSION_INSTALLATION_FAILED': {
                this.#logger.log({
                  level: MessageLevel.Error,
                  message: `Error while installing application update ${SERVICE_WORKER_TAG}`,
                  data: {
                    [DETAILS_KEY]: {
                      versionEvent,
                    },
                  },
                });

                this.#ngZone.run(() => {
                  this.#sharedFacade.enqueueInfoSnackbar({
                    translationKey: marker(
                      'shared.versionInstallationFailedMessageWithHtml',
                    ),
                    // TODO: move to a constant
                    priority: 1_000_001,
                    canBeDismissed: false,
                  });
                });

                break;
              }
              default: {
                this.#logger.log({
                  level: MessageLevel.Error,
                  message: `Unknown service worker version event ${SERVICE_WORKER_TAG}`,
                  data: {
                    [DETAILS_KEY]: {
                      versionEvent,
                    },
                  },
                });

                break;
              }
            }
          }),
          takeUntilDestroyed(this.#destroyReference),
        )
        .subscribe();
    });
  }

  #listenForServiceWorkerUnrecoverableStateEvents(): void {
    this.#ngZone.runOutsideAngular(() => {
      this.#swUpdate.unrecoverable
        .pipe(
          tap((unrecoverableStateEvent) => {
            this.#logger.log({
              level: MessageLevel.Error,
              message: `Unrecoverable state of service worker ${SERVICE_WORKER_TAG}`,
              data: {
                [DETAILS_KEY]: {
                  unrecoverableStateEvent,
                },
              },
            });

            this.#ngZone.run(() => {
              this.#dialog.open(ReloadThisPageModalComponent, {
                width: '400px',
              });
            });
          }),
          takeUntilDestroyed(this.#destroyReference),
        )
        .subscribe();
    });
  }

  #initializePendoIfEnabled(): void {
    if (!environment.usePendo) {
      return;
    }

    this.#ngZone.runOutsideAngular(() => {
      pendo.initialize();
    });
  }
}
